/**
 * QRContinuity — the doorway out into the wider EventDrop world.
 *
 * Rendered after every completed capture flow. Displays a single
 * QR code linked to tonight's gallery, the editorial preservation
 * line, and a quiet "pass to the next memory" press that returns
 * the booth to the launcher.
 *
 * Copy rules (constitutional):
 *   • Online preservation → "Your memory has been preserved ✦"
 *   • Offline / deferred  → "Preserved locally ✦"
 * Never any other variant.
 *
 * Auto-returns after 24s of dwell so the next guest sees the calm
 * launcher, not a stale QR.
 *
 * Behaviour preserved verbatim from the previous inline component.
 */
import React, { useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { SERIF, Eyebrow, Heading, Lede, GoldPill } from './boothShellAtoms';

const AUTO_RETURN_MS = 24_000;

const QRContinuity = ({ theme, event, flowId, deferred = false, onReturn }) => {
  const galleryUrl = `${typeof window !== 'undefined' ? window.location.origin : 'https://eventdrop.co'}/event/${event.slug}`;
  // Auto-return to the launcher after a longer dwell so the next
  // guest sees the calm launcher, not a stale QR.
  useEffect(() => {
    const t = setTimeout(() => onReturn(), AUTO_RETURN_MS);
    return () => clearTimeout(t);
  }, [onReturn]);

  return (
    <div
      data-testid="booth-qr-continuity"
      style={{
        minHeight: '100vh',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', padding: 'clamp(40px, 6vw, 80px) 24px',
        animation: 'editorialFade .6s ease-out',
      }}
    >
      <Eyebrow theme={theme}>✦ PRESERVED</Eyebrow>
      <Heading theme={theme}>
        {deferred ? 'Preserved locally ✦' : 'Your memory has been preserved ✦'}
      </Heading>
      <Lede theme={theme}>
        It’s on the wall now. Scan to see tonight’s gallery — or
        pass the booth on and another night will land beside yours.
      </Lede>

      <div
        style={{
          marginTop: 'clamp(28px, 4vw, 48px)',
          padding: 'clamp(20px, 2.4vw, 30px)',
          background: theme.polaroidFrame,
          borderRadius: 6,
          boxShadow: '0 24px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.55)',
          display: 'inline-block',
        }}
      >
        <QRCodeSVG
          value={galleryUrl}
          size={Math.min(280, typeof window !== 'undefined' ? window.innerWidth * 0.5 : 280)}
          level="M"
          fgColor={theme.bg}
          bgColor={theme.polaroidFrame}
          data-testid="booth-qr-svg"
        />
        <div style={{
          marginTop: 14, fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 13, color: '#3F2A14', letterSpacing: '0.16em',
          textTransform: 'uppercase',
        }}>
          ✦ {event.event_name || 'Tonight'} ✦
        </div>
      </div>

      <p style={{
        marginTop: 26, fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 14, color: theme.inkMuted, letterSpacing: '0.04em',
      }}>
        See tonight’s gallery ✦
      </p>

      <div style={{ marginTop: 32 }}>
        <GoldPill theme={theme} onClick={onReturn} large testId="booth-return-launcher">
          Pass to the next memory ✦
        </GoldPill>
      </div>
    </div>
  );
};

export default QRContinuity;
